import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { showToast } from "@/components/ui/Toast";
import {
  openConsultaWebSocket,
  startConsultaNits,
  type ProgresoConsulta,
  type WsMensaje,
} from "@/api/confecamaras";

const MAX_NITS = 100;

const parseNitsFromText = (raw: string): string[] => {
  const lines = raw
    .split(/[\n,;\s]+/)
    .map((l) => l.trim())
    .filter(Boolean);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of lines) {
    const digits = line.replace(/\D/g, "");
    if (!digits) continue;
    if (seen.has(digits)) continue;
    seen.add(digits);
    out.push(digits);
    if (out.length >= MAX_NITS) break;
  }
  return out;
};

const parseNitsFromFile = async (_file: File): Promise<string[]> => {
  // El parser real (.xlsx/.csv) corre en el backend. El frontend sólo
  // envía el archivo y deja que el servidor extraiga los NITs.
  return [];
};

type Estado =
  | { tipo: "idle" }
  | { tipo: "iniciando" }
  | { tipo: "procesando"; total: number; procesados: number }
  | { tipo: "finalizado"; total: number; resultados: ProgresoConsulta[] }
  | { tipo: "error"; mensaje: string };

interface Props {
  onResultados: (resultados: ProgresoConsulta[]) => void;
}

export interface ConsultaNitsFormHandle {
  reset: () => void;
}

export const ConsultaNitsForm = forwardRef<ConsultaNitsFormHandle, Props>(
  function ConsultaNitsForm({ onResultados }, ref) {
  const [texto, setTexto] = useState("");
  const [archivo, setArchivo] = useState<File | null>(null);
  const [estado, setEstado] = useState<Estado>({ tipo: "idle" });
  const socketRef = useRef<WebSocket | null>(null);

  const nitsDetectados = useMemo(() => parseNitsFromText(texto), [texto]);

  const onArchivo = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setArchivo(file);
  }, []);

  const detener = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
    setEstado({ tipo: "idle" });
  }, []);

  const reset = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
    setTexto("");
    setArchivo(null);
    setEstado({ tipo: "idle" });
    onResultados([]);
  }, [onResultados]);

  useImperativeHandle(ref, () => ({ reset }), [reset]);

  const iniciar = useCallback(async () => {
    let nits = nitsDetectados;
    if (archivo) {
      try {
        nits = await parseNitsFromFile(archivo);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Error leyendo archivo";
        showToast("error", "Archivo inválido", msg);
        return;
      }
    }

    if (!archivo && nits.length === 0) {
      showToast("error", "Sin NITs", "Ingresa al menos un NIT");
      return;
    }

    setEstado({ tipo: "iniciando" });
    const resultados: ProgresoConsulta[] = [];

    try {
      const { client_id, total } = await startConsultaNits({ nits, file: archivo });
      setEstado({ tipo: "procesando", total, procesados: 0 });

      const socket = openConsultaWebSocket(client_id, (msg: WsMensaje) => {
        if (msg.type === "start") {
          setEstado({ tipo: "procesando", total: msg.total, procesados: 0 });
        } else if (msg.type === "progress") {
          resultados.push(msg);
          onResultados([...resultados]);
          setEstado((prev) =>
            prev.tipo === "procesando"
              ? { ...prev, procesados: prev.procesados + 1 }
              : prev,
          );
        } else if (msg.type === "done") {
          setEstado({
            tipo: "finalizado",
            total: msg.total_ok + msg.total_not_found + msg.total_error,
            resultados,
          });
          showToast(
            "success",
            "Consulta finalizada",
            `${msg.total_ok} OK · ${msg.total_not_found} sin datos · ${msg.total_error} error`,
          );
        } else if (msg.type === "error") {
          showToast("error", "Error", msg.message);
          setEstado({ tipo: "error", mensaje: msg.message });
        }
      });
      socketRef.current = socket;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "No se pudo iniciar la consulta";
      showToast("error", "Error", msg);
      setEstado({ tipo: "error", mensaje: msg });
    }
  }, [archivo, nitsDetectados, onResultados]);

  const procesando = estado.tipo === "procesando" || estado.tipo === "iniciando";
  const progresoPct =
    estado.tipo === "procesando" && estado.total > 0
      ? Math.round((estado.procesados / estado.total) * 100)
      : estado.tipo === "finalizado"
        ? 100
        : 0;

  return (
    <div className="space-y-4 rounded-lg bg-white p-6 shadow-card">
      <div>
        <label
          htmlFor="confecamaras-nits"
          className="block text-sm font-medium text-ink-primary"
        >
          NITs (uno por línea, máximo {MAX_NITS})
        </label>
        <textarea
          id="confecamaras-nits"
          rows={6}
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono shadow-sm focus:border-accent-blue focus:outline-none focus:ring-1 focus:ring-accent-blue"
          placeholder={"900123456\n800987654\n1234567890"}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          disabled={procesando}
        />
        <p className="mt-1 text-xs text-ink-secondary">
          {nitsDetectados.length} NIT{nitsDetectados.length === 1 ? "" : "s"} detectados
        </p>
      </div>

      <div>
        <label
          htmlFor="confecamaras-file"
          className="block text-sm font-medium text-ink-primary"
        >
          …o sube un archivo (.xlsx o .csv)
        </label>
        <input
          id="confecamaras-file"
          type="file"
          accept=".xlsx,.csv"
          onChange={onArchivo}
          disabled={procesando}
          className="mt-1 block w-full text-sm text-ink-secondary file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium"
        />
        {archivo ? (
          <p className="mt-1 text-xs text-ink-secondary">Archivo: {archivo.name}</p>
        ) : null}
      </div>

      <div className="flex items-center gap-3">
        {procesando ? (
          <Button type="button" color="red" onClick={detener}>
            Cancelar
          </Button>
        ) : (
          <Button
            type="button"
            onClick={iniciar}
            disabled={nitsDetectados.length === 0 && !archivo}
          >
            Consultar
          </Button>
        )}
        {estado.tipo === "procesando" ? (
          <span className="text-sm text-ink-secondary">
            {estado.procesados}/{estado.total} ({progresoPct}%)
          </span>
        ) : null}
        {estado.tipo === "finalizado" ? (
          <span className="text-sm text-ink-secondary">
            Finalizado: {estado.resultados.length} respuestas
          </span>
        ) : null}
      </div>

      {(estado.tipo === "procesando" || estado.tipo === "finalizado") && estado.total > 0 ? (
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
          <div
            className="h-full bg-accent-blue transition-[width] duration-200"
            style={{ width: `${progresoPct}%` }}
          />
        </div>
      ) : null}

      {estado.tipo === "error" ? (
        <p className="text-sm text-red-600">{estado.mensaje}</p>
      ) : null}
    </div>
  );
});
